/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// UpNextCard  (Phase 3, updated Phase 3.6)
//
// Featured "Up Next" hero card — shows the single most-important checklist
// item with a prominent primary action button + "Skip for now" link.
//
// Visual treatments (Phase 3.6):
//   - 4px accent strip on the left edge (action-type color, subtle glow)
//   - Larger icon container (56 × 56, borderRadius 14)
//   - Coloured dot before eyebrow label; OVERDUE dot pulses
//   - Bigger title (22px / 700) and slightly darker subtitle
//   - CTA button gains trailing arrow icon and stronger weight
//
// Eyebrow label:
//   OVERDUE             — due_at is in the past
//   UP NEXT · DUE TODAY — due_at is today (not yet past)
//   UP NEXT             — no due_at or due_at is future
//
// Animation:
//   Appear  — fade + translateY(6px→0), 240 ms, cubic-bezier(0.32, 0.72, 0, 1)
//   Breathe — 1.8s box-shadow pulse (action-colour tinted), paused on hover
//   Both honour prefers-reduced-motion.
//
// Primary action button is a STUB in Phase 3 — fires onActionClick(item).
// Phase 4 wires the action panel.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import moment from 'moment';
import {
    Box, Typography, Button, Stack, Tooltip, useMediaQuery,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Iconify from 'src/components/Iconify';
import { CRM_PRIMARY_BUTTON_SX } from 'src/styles/crmButtons';
import { ACTION_META, DEFAULT_ACTION_META } from './actionMeta';
import { EMAIL_ACTION_TYPES } from './ActionPanel';
import TaskAssigneeRow from './TaskAssigneeRow';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const APPEAR_MS = 240;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getEyebrow(item) {
    if (!item?.due_at) return { label: 'UP NEXT', color: '#6e6e73' };
    const now = new Date();
    const due = new Date(item.due_at);
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    if (due < now) return { label: 'OVERDUE', color: '#FF5630' };
    if (due <= endOfToday) return { label: 'UP NEXT · DUE TODAY', color: '#FFAB00' };
    return { label: 'UP NEXT', color: '#6e6e73' };
}

// Primary button label per action type.
const ACTION_BUTTON_LABELS = {
    EMAIL_SENT:          'Compose email',
    WHATSAPP_SENT:       'Send WhatsApp',
    SMS_SENT:            'Send SMS',
    PHONE_CALLED:        'Log call',
    LOG_PHONE_CALL:      'Log call',
    SEND_CONSENT_FORM:   'Send consent',
    SEND_REVIEW_REQUEST: 'Send review request',
    SEND_PAYMENT_LINK:   'Send payment link',
    SEND_TREATMENT_PLAN: 'Send treatment plan',
    ADD_NOTE:            'Add note',
    SCHEDULE_REMINDER:   'Set reminder',
    LEAD_NEXT_ACTION:    'Set next action',
    BOOK_APPOINTMENT:    'Book appointment',
    CONFIRMATION:        'Mark confirmed',
    UPLOAD_DOCUMENT:     'Upload document',
    LINKED_CHECKLIST:    'Open checklist',
    FREE_TEXT:           'Enter text',
    MULTIPLE_CHOICE:     'Choose option',
    YES_NO_TOGGLE:       'Mark done',
    CURRENCY_AMOUNT:     'Enter amount',
    PAYMENT_METHOD:      'Set method',
    STAFF_PICKER:        'Assign staff',
    DATE_FIELD:          'Set date',
};

// Returns a { label, color } chip for the "next up" due-date display.
function getNextDueChip(item) {
    if (!item?.due_at) return null;
    const d = new Date(item.due_at);
    const now = new Date();
    const eot = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    if (d < now) return { label: 'Overdue', color: '#FF5630' };
    if (d <= eot) return { label: 'Due today', color: '#FFAB00' };
    return { label: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), color: '#6e6e73' };
}

// ---------------------------------------------------------------------------

export default function UpNextCard({ item, onActionClick, onSkip, leadEmail, actionBtnRef, onChangeDueDate, nextItem, nextEmptyReason, onItemClick, practiceId }) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [appeared, setAppeared] = useState(false);
    // Shared due-date dialog — tracks which item (featured or next-up) is being edited.
    const [calItem, setCalItem] = useState(null);
    const [calValue, setCalValue] = useState(null);

    const handleOpenDueDate = (targetItem) => {
        setCalValue(targetItem?.due_at ? moment(targetItem.due_at) : null);
        setCalItem(targetItem || item);
    };
    const handleDueDateCancel = () => setCalItem(null);
    const handleDueDateConfirm = () => {
        if (calValue && onChangeDueDate && calItem) {
            onChangeDueDate(calItem, calValue.toDate());
        }
        setCalItem(null);
    };

    // Trigger appear animation on mount (and whenever item identity changes —
    // parent passes a new key so this component remounts on promote).
    useEffect(() => {
        const t = setTimeout(() => setAppeared(true), 16);
        return () => clearTimeout(t);
    }, []);

    if (!item) return null;

    const meta = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    const eyebrow = getEyebrow(item);
    const title = item.title || item.name || meta.label;
    const isEmailType = EMAIL_ACTION_TYPES.has(item.item_type);
    // Only block when the orchestrator explicitly passes an empty leadEmail string.
    // If leadEmail is undefined (prop absent), the guard is inactive.
    const noEmailBlocked = isEmailType && leadEmail !== undefined && !leadEmail;
    let buttonLabel = ACTION_BUTTON_LABELS[item.item_type] || 'Take action';
    if (noEmailBlocked) buttonLabel = 'Add email first';
    const isOverdue = eyebrow.label === 'OVERDUE';

    // ---------------------------------------------------------------------------
    // Breathe keyframes — action-colour tinted, 1.8s.
    // ---------------------------------------------------------------------------
    const breatheSx = reducedMotion
        ? {}
        : {
            '@keyframes upnext-breathe': {
                '0%, 100%': { boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)' },
                '50%':       { boxShadow: `0 4px 20px ${meta.color}1A, 0 8px 28px rgba(0,0,0,0.10)` },
            },
            animation: appeared ? 'upnext-breathe 1.8s ease-in-out infinite' : 'none',
            '&:hover': { animationPlayState: 'paused' },
        };

    // Accent strip glow keyframes.
    const accentGlowSx = reducedMotion
        ? {}
        : {
            '@keyframes accent-glow': {
                '0%, 100%': { boxShadow: 'none' },
                '50%':       { boxShadow: `2px 0 8px ${meta.color}66` },
            },
            animation: 'accent-glow 1.5s ease-in-out infinite',
        };

    // OVERDUE dot pulse keyframes.
    const overdueDotsxProps = isOverdue && !reducedMotion
        ? {
            '@keyframes overdue-pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%':       { transform: 'scale(1.4)', opacity: 0.4 },
            },
            animation: 'overdue-pulse 1.4s ease-in-out infinite',
        }
        : {};

    const titleId = `upnext-title-${item.id ?? item.sort_order ?? 'card'}`;

    // Right pane — "Next up" preview or "All caught up" placeholder
    const showRightPane = !!(nextItem || nextEmptyReason);
    const nextMeta = nextItem ? (ACTION_META[nextItem.item_type] || DEFAULT_ACTION_META) : null;
    const nextTitle = nextItem ? (nextItem.title || nextItem.name || nextMeta.label) : null;
    const nextDueChip = getNextDueChip(nextItem);
    const nextPaneSx = nextItem && !reducedMotion
        ? {
            '@keyframes next-slide-in': {
                from: { opacity: 0, transform: 'translateX(-8px)' },
                to:   { opacity: 1, transform: 'translateX(0)' },
            },
            animation: 'next-slide-in 240ms cubic-bezier(0.32, 0.72, 0, 1) forwards',
        }
        : {};

    return (
        <Box
            data-testid="upnext-card"
            role="article"
            aria-labelledby={titleId}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                bgcolor: '#ffffff',
                borderRadius: '16px',
                p: 3,
                minHeight: 160,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
                opacity: appeared ? 1 : 0,
                transform: appeared ? 'none' : 'translateY(6px)',
                transition: reducedMotion
                    ? 'none'
                    : `opacity ${APPEAR_MS}ms ${EASING}, transform ${APPEAR_MS}ms ${EASING}`,
                ...breatheSx,
            }}
        >
            {/* Left accent strip */}
            <Box
                data-testid="upnext-accent-strip"
                sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    bgcolor: meta.color,
                    borderRadius: '16px 0 0 16px',
                    ...accentGlowSx,
                }}
            />

            {/* 70 / 30 split row */}
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>

            {/* ── Left column (featured task) ─────────────────────────── */}
            <Box sx={{ flex: showRightPane ? '0 0 calc(70% - 9px)' : '1', minWidth: 0 }}>

            {/* Eyebrow (dot + label) */}
            <Box
                data-testid="upnext-eyebrow"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}
            >
                <Box
                    data-testid="upnext-eyebrow-dot"
                    data-overdue={isOverdue ? 'true' : undefined}
                    sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: eyebrow.color,
                        flexShrink: 0,
                        ...overdueDotsxProps,
                    }}
                />
                <Typography
                    component="span"
                    sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: eyebrow.color,
                    }}
                >
                    {eyebrow.label}
                </Typography>
            </Box>

            {/* Icon + Title */}
            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2 }}>
                <Box
                    data-testid="upnext-icon"
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '14px',
                        bgcolor: `${meta.color}1A`,
                        border: `1.5px solid ${meta.color}33`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mt: 0.25,
                    }}
                >
                    <Iconify icon={meta.icon} width={28} height={28} sx={{ color: meta.color }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id={titleId}
                        sx={{
                            fontSize: '22px',
                            fontWeight: 700,
                            color: '#1d1d1f',
                            letterSpacing: '-0.022em',
                            lineHeight: 1.25,
                        }}
                    >
                        {title}
                    </Typography>
                    {item.description && (
                        <Typography
                            sx={{
                                fontSize: '13px',
                                color: '#3c3c43',
                                letterSpacing: '-0.005em',
                                mt: 0.5,
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.description}
                        </Typography>
                    )}
                    {item?.due_at && (
                        <Typography
                            data-testid="upnext-featured-due-timestamp"
                            sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#86868b',
                                letterSpacing: '-0.005em',
                                mt: 0.75,
                            }}
                        >
                            {moment(item.due_at).format('dddd D MMMM, HH:mm')}
                        </Typography>
                    )}
                </Box>
            </Stack>

            {/* Action row */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
                <Tooltip
                    title={noEmailBlocked ? 'This lead has no email on file' : ''}
                    placement="top"
                    arrow
                    disableHoverListener={!noEmailBlocked}
                >
                    {/* span required for Tooltip to work on a disabled button */}
                    <span>
                        <Button
                            ref={actionBtnRef}
                            data-testid="upnext-action-btn"
                            variant="contained"
                            disableElevation
                            disabled={noEmailBlocked}
                            endIcon={!noEmailBlocked && <Iconify icon="mdi:arrow-right" width={16} height={16} />}
                            onClick={!noEmailBlocked ? () => onActionClick && onActionClick(item) : undefined}
                            sx={{
                                ...CRM_PRIMARY_BUTTON_SX,
                                fontSize: '14px',
                                fontWeight: 700,
                                minWidth: 140,
                                py: 1,
                            }}
                        >
                            {buttonLabel}
                        </Button>
                    </span>
                </Tooltip>
                <Typography
                    data-testid="upnext-skip-btn"
                    component="button"
                    type="button"
                    aria-label="Skip this task for now"
                    onClick={() => onSkip && onSkip(item)}
                    sx={{
                        fontSize: '13px',
                        color: '#6e6e73',
                        letterSpacing: '-0.01em',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        p: 0,
                        fontFamily: 'inherit',
                        '&:hover': { color: '#1d1d1f', textDecoration: 'underline' },
                    }}
                >
                    Skip for now
                </Typography>
                {onChangeDueDate && (
                    <Tooltip title="Change due date" placement="top" arrow>
                        <IconButton
                            data-testid="upnext-change-due-date-btn"
                            size="small"
                            aria-label="Change due date"
                            onClick={() => handleOpenDueDate(item)}
                            sx={{ color: '#6e6e73', '&:hover': { color: '#1d1d1f' } }}
                        >
                            <Iconify icon="mdi:calendar-edit" width={16} height={16} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {/* Assignee row — editable, shown when task has a taskId */}
            {item.taskId && practiceId && (
                <Box sx={{ mt: 1.25 }}>
                    <TaskAssigneeRow
                        data-testid="featured-task-assignee-row"
                        practiceId={practiceId}
                        taskId={item.taskId}
                        initialAssignees={item.rawTask?.assignees || []}
                        size={24}
                    />
                </Box>
            )}
            </Box>{/* /left column */}

            {/* ── Vertical divider ────────────────────────────────────── */}
            {showRightPane && (
                <Box
                    aria-hidden="true"
                    sx={{ width: '1px', bgcolor: 'rgba(0,0,0,0.06)', flexShrink: 0 }}
                />
            )}

            {/* ── Right column (next up preview) ──────────────────────── */}
            {nextItem && (
                <Box
                    data-testid="upnext-next-pane"
                    role="button"
                    tabIndex={0}
                    aria-label={`Next up: ${nextTitle}`}
                    onClick={() => onItemClick && onItemClick(nextItem)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { if (onItemClick) onItemClick(nextItem); } }}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 1,
                        cursor: onItemClick ? 'pointer' : 'default',
                        borderRadius: '8px',
                        px: 1,
                        py: 0.5,
                        '&:hover': onItemClick ? { bgcolor: 'rgba(0,0,0,0.025)' } : {},
                        ...nextPaneSx,
                    }}
                >
                    {/* Eyebrow: NEXT UP */}
                    <Typography
                        data-testid="upnext-next-eyebrow"
                        component="span"
                        sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: '#6e6e73',
                        }}
                    >
                        Next up
                    </Typography>

                    {/* Compact icon */}
                    <Box
                        data-testid="upnext-next-icon"
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '6px',
                            bgcolor: `${nextMeta.color}1A`,
                            border: `1px solid ${nextMeta.color}33`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Iconify icon={nextMeta.icon} width={13} height={13} sx={{ color: nextMeta.color }} />
                    </Box>

                    {/* Title — single line, truncated */}
                    <Typography
                        data-testid="upnext-next-title"
                        sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#1d1d1f',
                            letterSpacing: '-0.01em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {nextTitle}
                    </Typography>

                    {/* Due date chip */}
                    {nextDueChip && (
                        <Typography
                            data-testid="upnext-next-due-chip"
                            sx={{
                                fontSize: '11px',
                                fontWeight: 500,
                                color: nextDueChip.color,
                                letterSpacing: '-0.005em',
                            }}
                        >
                            {nextDueChip.label}
                        </Typography>
                    )}

                    {/* Absolute timestamp — precision companion to the relative chip */}
                    {nextItem?.due_at && (
                        <Typography
                            data-testid="upnext-next-due-timestamp"
                            sx={{
                                fontSize: '11px',
                                fontWeight: 400,
                                color: '#86868b',
                                letterSpacing: '-0.005em',
                            }}
                        >
                            {moment(nextItem.due_at).format('ddd D MMM, HH:mm')}
                        </Typography>
                    )}

                    {/* Calendar button for next-up item */}
                    {onChangeDueDate && (
                        <Tooltip title="Change due date" placement="top" arrow>
                            <IconButton
                                data-testid="upnext-next-change-due-date-btn"
                                size="small"
                                aria-label="Change next task due date"
                                onClick={(e) => { e.stopPropagation(); handleOpenDueDate(nextItem); }}
                                sx={{ color: '#6e6e73', '&:hover': { color: '#1d1d1f' }, alignSelf: 'flex-start', mt: 0.25 }}
                            >
                                <Iconify icon="mdi:calendar-edit" width={14} height={14} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Read-only assignee micro-avatars for next-up task */}
                    {nextItem?.taskId && practiceId && nextItem?.rawTask?.assignees?.length > 0 && (
                        <TaskAssigneeRow
                            data-testid="next-task-assignee-row"
                            practiceId={practiceId}
                            taskId={nextItem.taskId}
                            initialAssignees={nextItem.rawTask.assignees}
                            size={18}
                            readOnly
                        />
                    )}
                </Box>
            )}

            {/* ── Right column — "All caught up" placeholder ──────────── */}
            {!nextItem && nextEmptyReason === 'caught_up' && (
                <Box
                    data-testid="upnext-next-pane-empty"
                    aria-label="All caught up — nothing else queued"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 0.75,
                        px: 1,
                        py: 0.5,
                        opacity: 0.5,
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: '#6e6e73',
                        }}
                    >
                        Next up
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Iconify icon="mdi:check-circle-outline" width={14} height={14} sx={{ color: '#34c759', flexShrink: 0 }} />
                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#6e6e73',
                                letterSpacing: '-0.005em',
                            }}
                        >
                            All caught up
                        </Typography>
                    </Box>
                </Box>
            )}

            </Box>{/* /split row */}

            {/* Due-date change dialog — shared for featured and next-up items */}
            <Dialog
                data-testid="upnext-due-date-dialog"
                open={calItem != null}
                onClose={handleDueDateCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontSize: '16px', fontWeight: 700, pb: 1 }}>
                    Change due date
                </DialogTitle>
                <DialogContent sx={{ pt: 1.5 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            label="New due date"
                            value={calValue}
                            onChange={(newVal) => setCalValue(newVal)}
                            slotProps={{ textField: { size: 'small', fullWidth: true, 'data-testid': 'upnext-due-date-picker-input' } }}
                        />
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        data-testid="upnext-due-date-cancel"
                        onClick={handleDueDateCancel}
                        sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-testid="upnext-due-date-confirm"
                        variant="contained"
                        disableElevation
                        disabled={!calValue}
                        onClick={handleDueDateConfirm}
                        sx={{ ...CRM_PRIMARY_BUTTON_SX, textTransform: 'none', fontSize: '14px', py: 0.75 }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

UpNextCard.propTypes = {
    /** Checklist item to feature. If null, card does not render. */
    item: PropTypes.object,
    /** Called with item when primary action button is clicked. */
    onActionClick: PropTypes.func,
    /** Called with item when "Skip for now" is clicked. */
    onSkip: PropTypes.func,
    /** Lead's email address — when empty and item is an email type, CTA is blocked. */
    leadEmail: PropTypes.string,
    /** Ref forwarded to the primary action button — used by orchestrator for focus return. */
    actionBtnRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    /** Called with (item, newDate: Date) when user confirms a due-date change. */
    onChangeDueDate: PropTypes.func,
    /** The next-in-queue checklist item to preview in the right 30% pane. */
    nextItem: PropTypes.object,
    /** When nextItem is null, reason the pane is empty — 'caught_up' renders placeholder. */
    nextEmptyReason: PropTypes.oneOf(['caught_up']),
    /** Called with the next item when user clicks the right pane to promote it. */
    onItemClick: PropTypes.func,
    /** Practice ID forwarded to TaskAssigneeRow for PATCH calls. */
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

UpNextCard.defaultProps = {
    item: null,
    onActionClick: null,
    onSkip: null,
    leadEmail: undefined,
    actionBtnRef: null,
    onChangeDueDate: null,
    nextItem: null,
    nextEmptyReason: null,
    onItemClick: null,
    practiceId: null,
};
